import React, { useEffect, useState } from 'react';
import { ProjectTeamType } from 'domain/shared/newProject';
import {
  AssignmentModalInitialData,
  SetStaffingProjectData,
  StaffingAssignment,
  StaffingProjectAssignment,
  StaffingProjectData,
} from 'domain/shared/staffing';
import { mapTeamStructureWithAssignments } from 'domain/shared/assignment/mapTeamStructureWithAssignments';
import { DeleteStaffingAssignmentModal } from 'domain/shared/assignment';
import UpdateStaffingAssignmentModal from './UpdateStaffingAssignmentModal';
import { EMPTY_VALUE_INDICATOR } from 'framework/utils';

interface UpdateStaffingAssignmentModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  projectData: StaffingProjectData;
  setProjectData?: SetStaffingProjectData;
  isVirtual?: boolean;
  initialData: AssignmentModalInitialData;
}

const UpdateDeleteStaffingAssignmentModal = ({
  open,
  setOpen,
  projectData,
  setProjectData,
  isVirtual,
  initialData,
}: UpdateStaffingAssignmentModalProps) => {
  const [isUpdateModalOpen, setUpdateModalOpen] = useState(open);
  const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setUpdateModalOpen(open);
    } else {
      setUpdateModalOpen(false);
      setDeleteConfirmOpen(false);
    }
  }, [open]);

  const onUpdateModalClose = () => {
    setUpdateModalOpen(false);
    setDeleteConfirmOpen(false);
    setOpen(false);
  };

  const onUpdateAfter = async (response: StaffingAssignment) => {
    const currentAssignments = (projectData.assignments || []) as StaffingProjectAssignment[];
    const nextAssignments = currentAssignments.map(assignment =>
      String(assignment.id) === String(initialData.id)
        ? {
            ...assignment,
            id: String(response?.id ?? initialData.id),
            employeeAlias: response?.employeeAlias,
            positionId: response?.positionId,
            technologyId: response?.technologyId,
            isBackup: response?.isBackup ?? false,
            isSpof: response?.isSpof,
            isBench: response?.isBench ?? false,
            projectId: assignment.projectId ?? projectData.id,
            projectName: assignment.projectName ?? projectData.name ?? '',
            fromDate: response?.fromDate,
            toDate: response?.toDate,
            maxPercent: response?.maxPercent,
            slotId: response?.slotId,
            status: response?.status,
            membershipRole: response?.membershipRole,
            competencyLevel: response?.competencyLevel,
            isProbation: response?.isProbation ?? false,
            noteText: response?.noteText ?? null,
          }
        : assignment,
    );
    updateProjectData(nextAssignments);
  };

  const onDeleteAfter = async (assignmentId: string) => {
    const currentAssignments = (projectData.assignments || []) as StaffingProjectAssignment[];
    const nextAssignments = currentAssignments.filter(assign => String(assign.id) !== assignmentId);
    updateProjectData(nextAssignments);
  };

  const updateProjectData = (updatedAssignments: StaffingProjectAssignment[]) => {
    const nextProjectData = {
      ...projectData,
      assignments: updatedAssignments,
    };

    const enhancedResponse = mapTeamStructureWithAssignments(
      nextProjectData,
      projectData.team as ProjectTeamType | undefined,
    );

    setProjectData?.({
      ...nextProjectData,
      ...enhancedResponse,
    });
  };

  return (
    <>
      <UpdateStaffingAssignmentModal
        initialData={{
          ...initialData,
          target: {
            id: projectData.legacyId,
            type: 'PROJECT',
            displayName: projectData.name || EMPTY_VALUE_INDICATOR,
          },
        }}
        isVirtual={isVirtual}
        isOpen={isUpdateModalOpen}
        onClose={onUpdateModalClose}
        onUpdateAfter={onUpdateAfter}
        onDeleteClick={() => {
          setUpdateModalOpen(false);
          setDeleteConfirmOpen(true);
        }}
      />
      <DeleteStaffingAssignmentModal
        assignmentId={String(initialData.id)}
        isOpen={isDeleteConfirmOpen}
        onDeleteAfter={onDeleteAfter}
        onClose={onUpdateModalClose}
        onCancelClick={() => {
          setDeleteConfirmOpen(false);
          setUpdateModalOpen(true);
        }}
      />
    </>
  );
};

export default UpdateDeleteStaffingAssignmentModal;
